import { createUrl, POST } from '@monorepo/utils/api'
import { authFetch } from '@monorepo/utils/auth-fetch'
import { auth, firestore } from '@monorepo/utils/firebase'
import { log } from '@monorepo/utils/utils'
import { FirebaseAuthTypes, linkWithCredential } from '@monorepo/utils/firebase'
import { collection, doc, getDoc, updateDoc } from '@monorepo/utils/firebase'
import { clearStoredAnonymousUserId, getStoredAnonymousUserId } from './storeAnonymousUser'

type AuthCredential = FirebaseAuthTypes.AuthCredential
type UserCredential = FirebaseAuthTypes.UserCredential

// Store anonymous UID for data migration after sign-in
let pendingAnonymousUserId: string | null = null

/**
 * Utility function to handle authentication with credential
 * If user is anonymous, it tries to link the credential to maintain the user ID
 * If the credential is already associated with an existing account,
 * it signs in with that account instead and migrates data from the anonymous account
 * Otherwise, it signs in with the credential
 */
export const handleAuthWithCredential = async (
  credential: AuthCredential,
  signInFunction: () => Promise<UserCredential>,
  email?: string | null // Optional email for cases like email/password auth
): Promise<UserCredential> => {
  const currentUser = auth().currentUser

  if (currentUser && currentUser.isAnonymous) {
    // Store the anonymous user ID for potential migration
    const anonymousUserId = currentUser.uid

    // Try to link the credential with the anonymous account
    try {
      const result = await linkWithCredential(currentUser, credential)
      log('Credential linked to anonymous user:', result)
      // Update user document to mark as non-anonymous and update login time
      // Credits are already handled during the link process, no need to add more
      await updateDoc(doc(collection(firestore(), 'users'), result.user.uid), {
        email: result.user.email || email,
        isAnonymous: false,
        lastLoginAt: new Date().toUTCString(),
      })
      return result
    } catch (error: any) {
      console.error('Error linking anonymous user with credential:', error)

      // Check for existing account errors
      // Firebase error codes: https://firebase.google.com/docs/reference/js/auth#autherrorcodes
      if (
        error.code === 'auth/email-already-in-use' ||
        error.code === 'auth/credential-already-in-use' ||
        error.code === 'auth/account-exists-with-different-credential'
      ) {
        // If the credential is already in use, we should sign in with it
        log('Credential already in use, signing in with existing account')

        // Store the anonymous user ID for migration after sign-in
        pendingAnonymousUserId = anonymousUserId

        // Sign in with the existing account
        const result = await signInFunction()

        // After successful sign-in, migrate data from anonymous account to the authenticated account
        if (pendingAnonymousUserId && result.user) {
          try {
            await authFetch(createUrl(POST.USER.MIGRATE), {
              body: JSON.stringify({
                from: pendingAnonymousUserId,
                to: result.user.uid,
              }),
            })
            log(`Data migrated successfully from anonymous user ${pendingAnonymousUserId} to authenticated user ${result.user.uid}`)
          } catch (migrationError) {
            console.error('Error during data migration:', migrationError)
            // Don't throw here, we still want to complete the sign-in process
          } finally {
            // Clear the pending ID and stored ID after migration attempt
            pendingAnonymousUserId = null
            clearStoredAnonymousUserId()
          }
        }

        return result
      }

      throw error
    }
  } else {
    // Normal flow - sign in with credential
    const result = await signInFunction()

    // Check if we need to migrate data from a previous anonymous session
    // Either from a pending ID or from localStorage
    const storedAnonymousId = getStoredAnonymousUserId()
    const sourceId = pendingAnonymousUserId || storedAnonymousId

    if (sourceId && result.user) {
      try {
        await authFetch(createUrl(POST.USER.MIGRATE), {
          body: JSON.stringify({
            from: sourceId,
            to: result.user.uid,
          }),
        })
        log(`Data migrated successfully from anonymous user ${sourceId} to authenticated user ${result.user.uid}`)
      } catch (migrationError) {
        console.error('Error during data migration:', migrationError)
      } finally {
        // Clear both the pending ID and stored ID after migration attempt
        pendingAnonymousUserId = null
        clearStoredAnonymousUserId()
      }
    }

    return result
  }
}
